import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
    PiBuildings,
    PiChatCircle,
    PiClipboardText,
    PiCurrencyCircleDollar,
    PiFilePdf,
    PiFiles,
    PiLifebuoy,
    PiListBullets,
    PiPackage,
    PiPaperclip,
    PiShoppingCart,
} from 'react-icons/pi';
import { canAccessCommission, canApproveCommission, hasRole, isSuperAdmin } from '@v2/lib/auth';
import api from '@v2/lib/api';
import { canViewGluteleader } from '@v2/lib/auth';
import DealInstallmentsPanel from '@v2/pages/commissioning/DealInstallmentsPanel';
import GluteleaderDealCard from '@v2/pages/commissioning/GluteleaderDealCard';
import { useI18n } from '@v2/providers/I18nProvider';
import { cn } from '@v2/utils/cn';
import type { DealDetailTabKey, PloomesDealDetail } from '../types';
import {
    DealAttachmentsPanel,
    DealOrdersPanel,
    DealTasksPanel,
    DealTimelinePanel,
} from './dealRelationPanels';
import DetailCard from './DetailCard';
import DealPaymentMethodsCard from './DealPaymentMethodsCard';
import PloomesDealClientPanel from './PloomesDealClientPanel';
import PloomesDealCommissionCard from './PloomesDealCommissionCard';
import PloomesDealDocumentsQuotesPanel from './PloomesDealDocumentsQuotesPanel';
import PloomesDealFieldSectionsPanel from './PloomesDealFieldSectionsPanel';
import PloomesDealProductsSection from './PloomesDealProductsSection';
import PloomesDealZendeskPanel from './PloomesDealZendeskPanel';
import QuoteInstallmentsCard from './QuoteInstallmentsCard';
import { useDealRelations } from './useDealRelations';

type Props = {
    deal: PloomesDealDetail;
};

type TabDef = {
    key: DealDetailTabKey;
    labelKey: string;
    icon: typeof PiChatCircle;
    count?: number;
    visible: boolean;
};

const DEFAULT_TAB: DealDetailTabKey = 'products';

export default function PloomesDealDetailTabs({ deal }: Props) {
    const { t } = useI18n();
    const [searchParams, setSearchParams] = useSearchParams();
    const canCommission = canAccessCommission();
    const canFinance = canApproveCommission();
    const canViewPloomesFieldsTab = isSuperAdmin() || hasRole('admin');
    // Reunião 21/10: reserva só em pedido ganho, por Comex/admin (a flag global já é só Comex/admin).
    const canRequestStock = Boolean(window.__V2__?.can_request_stock_ploomes_deal) && deal.is_won === true;
    const { data: relations } = useDealRelations(deal.id);
    const zendeskCountQuery = useQuery({
        queryKey: ['ploomes-deal-zendesk-tickets', deal.id],
        queryFn: async ({ signal }) => {
            const { data } = await api.get<{
                data: unknown[];
                meta?: { zendesk_agent_url?: string | null };
            }>(`/ploomes-deals/${deal.id}/zendesk-tickets`, { signal });
            return {
                tickets: data.data,
                agentUrl: data.meta?.zendesk_agent_url ?? null,
            };
        },
        select: (data) => data.tickets.length,
        enabled: Boolean(deal.id) && (canCommission || canFinance),
        staleTime: 30_000,
    });

    const tabs = useMemo<TabDef[]>(() => {
        const interactionsCount = relations?.ploomes_interactions?.length ?? deal.ploomes_interactions?.length ?? 0;
        const tasksCount = relations?.ploomes_tasks?.length ?? deal.ploomes_tasks?.length ?? 0;
        const ordersCount = relations?.ploomes_orders?.length ?? deal.ploomes_orders?.length ?? 0;
        const attachmentsCount = relations?.ploomes_attachments?.length ?? deal.ploomes_attachments?.length ?? 0;
        const zendeskCount = zendeskCountQuery.data;

        const all: TabDef[] = [
            {
                key: 'products',
                labelKey: 'v2.ploomes_deals.tabs.products',
                icon: PiPackage,
                count: deal.order_products_count ?? undefined,
                visible: true,
            },
            {
                key: 'client',
                labelKey: 'v2.ploomes_deals.tabs.client',
                icon: PiBuildings,
                visible: true,
            },
            {
                key: 'commission',
                labelKey: 'v2.ploomes_deals.tabs.commission',
                icon: PiCurrencyCircleDollar,
                visible: canCommission || canFinance,
            },
            {
                key: 'proposals',
                labelKey: 'v2.ploomes_deals.tabs.proposals',
                icon: PiFilePdf,
                visible: true,
            },
            {
                key: 'orders',
                labelKey: 'v2.ploomes_deals.tabs.orders',
                icon: PiShoppingCart,
                count: ordersCount,
                visible: true,
            },
            {
                key: 'documents',
                labelKey: 'v2.ploomes_deals.tabs.documents',
                icon: PiFiles,
                visible: true,
            },
            {
                key: 'attachments',
                labelKey: 'v2.ploomes_deals.tabs.attachments',
                icon: PiPaperclip,
                count: attachmentsCount,
                visible: true,
            },
            {
                key: 'timeline',
                labelKey: 'v2.ploomes_deals.tabs.timeline',
                icon: PiChatCircle,
                count: interactionsCount,
                visible: true,
            },
            {
                key: 'tasks',
                labelKey: 'v2.ploomes_deals.tabs.tasks',
                icon: PiClipboardText,
                count: tasksCount,
                visible: true,
            },
            {
                key: 'fields',
                labelKey: 'v2.ploomes_deals.tabs.fields',
                icon: PiListBullets,
                visible: canViewPloomesFieldsTab,
            },
            {
                key: 'zendesk',
                labelKey: 'v2.ploomes_deals.tabs.zendesk',
                icon: PiLifebuoy,
                count: zendeskCount,
                visible: canCommission || canFinance,
            },
        ];

        return all.filter((tab) => tab.visible);
    }, [deal, relations, canCommission, canFinance, canViewPloomesFieldsTab, zendeskCountQuery.data]);

    const requestedRaw = searchParams.get('tab') || '';
    const requested = (
        requestedRaw === 'installments' || requestedRaw === 'parcelas' || requestedRaw === 'comissao'
            ? 'commission'
            : requestedRaw
    ) as DealDetailTabKey;

    const activeTab: DealDetailTabKey = tabs.some((tab) => tab.key === requested)
        ? requested
        : tabs.some((tab) => tab.key === DEFAULT_TAB)
          ? DEFAULT_TAB
          : (tabs[0]?.key ?? DEFAULT_TAB);

    const setTab = (key: DealDetailTabKey) => {
        const next = new URLSearchParams(searchParams);
        next.set('tab', key);
        setSearchParams(next, { replace: true });
    };

    return (
        <div className="space-y-4">
            <div className="sticky top-0 z-10 -mx-1 border-b border-gray-200 bg-white/95 px-1 py-2 backdrop-blur dark:border-zinc-800 dark:bg-zinc-950/95">
                <div className="flex items-center gap-2 overflow-x-auto whitespace-nowrap [scrollbar-width:thin]">
                    {tabs.map((tabItem) => {
                        const Icon = tabItem.icon;
                        const active = activeTab === tabItem.key;
                        return (
                            <button
                                key={tabItem.key}
                                type="button"
                                onClick={() => setTab(tabItem.key)}
                                className={cn(
                                    'inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium transition',
                                    active
                                        ? 'bg-orange-100 text-orange-700 dark:bg-orange-950/50 dark:text-orange-300'
                                        : 'text-gray-600 hover:bg-gray-100 dark:text-zinc-300 dark:hover:bg-zinc-800',
                                )}
                            >
                                <Icon className="h-4 w-4" />
                                {t(tabItem.labelKey)}
                                {tabItem.count != null && tabItem.count > 0 && (
                                    <span
                                        className={cn(
                                            'rounded-full px-1.5 py-0.5 text-[10px] font-bold',
                                            active
                                                ? 'bg-orange-200 text-orange-800 dark:bg-orange-900/60 dark:text-orange-200'
                                                : 'bg-gray-200 text-gray-600 dark:bg-zinc-700 dark:text-zinc-300',
                                        )}
                                    >
                                        {tabItem.count}
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>
            </div>

            <div className="min-h-[16rem]">
                {activeTab === 'products' && (
                    <PloomesDealProductsSection
                        dealId={deal.id}
                        contractTypeLabel={deal.contract_type_label}
                        productsCountHint={deal.order_products_count}
                        canRequestStock={canRequestStock}
                    />
                )}

                {activeTab === 'client' && <PloomesDealClientPanel deal={deal} />}

                {activeTab === 'commission' && (
                    <div
                        className={cn(
                            'grid items-start gap-6',
                            canCommission ? 'lg:grid-cols-12' : 'grid-cols-1',
                        )}
                    >
                        <div className={cn('min-w-0 space-y-6', canCommission && 'lg:col-span-7')}>
                            {canCommission && <DealPaymentMethodsCard dealId={deal.id} deal={deal} />}
                            {canCommission && <QuoteInstallmentsCard dealId={deal.id} />}
                            {canCommission && (
                                <DetailCard
                                    title={t(
                                        canFinance
                                            ? 'v2.ploomes_deals.tabs.installments_finance'
                                            : 'v2.ploomes_deals.tabs.installments_seller',
                                    )}
                                >
                                    <DealInstallmentsPanel
                                        dealId={deal.id}
                                        showLeadership={canFinance}
                                    />
                                </DetailCard>
                            )}
                        </div>
                        {canCommission && (
                            <div className="min-w-0 lg:col-span-5 lg:sticky lg:top-16 lg:self-start">
                                <PloomesDealCommissionCard
                                    dealId={deal.id}
                                    initialPreview={deal.commission_preview}
                                />
                                {canViewGluteleader() && (
                                    <div className="mt-6">
                                        <GluteleaderDealCard dealId={deal.id} />
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'zendesk' && <PloomesDealZendeskPanel dealId={deal.id} deal={deal} />}

                {activeTab === 'proposals' && (
                    <PloomesDealDocumentsQuotesPanel dealId={deal.id} mode="quotes" />
                )}

                {activeTab === 'orders' && (
                    <DetailCard title={t('v2.ploomes_deals.tabs.orders')}>
                        <DealOrdersPanel deal={deal} />
                    </DetailCard>
                )}

                {activeTab === 'documents' && (
                    <PloomesDealDocumentsQuotesPanel dealId={deal.id} mode="documents" />
                )}

                {activeTab === 'attachments' && (
                    <DetailCard title={t('v2.ploomes_deals.tabs.attachments')}>
                        <DealAttachmentsPanel deal={deal} />
                    </DetailCard>
                )}

                {activeTab === 'timeline' && (
                    <DetailCard title={t('v2.ploomes_deals.tabs.timeline')}>
                        <DealTimelinePanel deal={deal} />
                    </DetailCard>
                )}

                {activeTab === 'tasks' && (
                    <DetailCard title={t('v2.ploomes_deals.tabs.tasks')}>
                        <DealTasksPanel deal={deal} />
                    </DetailCard>
                )}

                {activeTab === 'fields' && <PloomesDealFieldSectionsPanel dealId={deal.id} />}
            </div>
        </div>
    );
}
